/**
 * Logging the four things the game cannot see (TASKS T68, T69, SPEC §8.4).
 *
 * > The game observes only one of five plan components. Without the other four, Balance is fiction.
 *
 * So this exists to make the other four cheap enough to actually record. The design constraint is a
 * number: **under ten seconds**, one-handed. That rules out a form, and it is why there is exactly
 * one required field.
 *
 *  - **Minutes is the only required field.** Everything else is optional, and a component with no
 *    entry renders as "not logged" rather than as zero (DoD-9) — the app can observe that he did not
 *    write it down, never that he did not do it.
 *  - **Never nag.** Nothing here reminds, prompts, or counts days since. A tracker that asked would
 *    become a thing to feel guilty about, which is the failure mode this whole app is shaped around.
 *  - **Repeat errors become items** (DoD-11). The highest-value path in the app: a mistake made in
 *    real conversation with a proficient speaker is the best possible review candidate, and it costs
 *    two taps to capture.
 *
 * Pure. No DOM, no clock, no store.
 */

import type { ItemRecord, ManualComponent, ManualEntry } from '../store/schema';
import { newMemory } from '../store/schema';

/**
 * The minute values offered as one-tap chips.
 *
 * A ronda is ninety seconds and a feed is six minutes, so the low end is where the resolution has to
 * be. Typing an exact number is still possible; these exist so that the common case is a single tap
 * rather than a keyboard.
 */
export const MINUTE_CHIPS: readonly number[] = [5, 10, 15, 20, 30, 45, 60];

export const COMPONENT_LABELS: Readonly<Record<ManualComponent, string>> = {
  course: 'Course study',
  input: 'Listening and reading',
  conversation: 'Conversation with Diana',
  narration: 'Narrating out loud',
};

/** Minutes must be a real, positive, plausible number. Nothing else is required. */
export const MAX_MINUTES = 600;

export type MinutesParse = { ok: true; minutes: number } | { ok: false; reason: string };

/**
 * Parse the one required field.
 *
 * Zero is refused rather than accepted, and the distinction matters: an entry of zero minutes is
 * indistinguishable from not having logged, and "not logged" is a state the tracker already has a
 * truthful way to say. Recording a zero would make it say something false instead.
 */
export function parseMinutes(raw: string): MinutesParse {
  const text = raw.trim();
  if (text.length === 0) return { ok: false, reason: 'Minutes is the one thing this needs.' };
  const value = Number(text);
  if (!Number.isFinite(value)) return { ok: false, reason: 'Minutes has to be a number.' };
  if (value <= 0) {
    return { ok: false, reason: 'Zero minutes is the same as not logging it, which is fine too.' };
  }
  if (value > MAX_MINUTES) return { ok: false, reason: `More than ${MAX_MINUTES} minutes?` };
  return { ok: true, minutes: Math.round(value) };
}

export interface LogDraft {
  component: ManualComponent;
  minutes: string;
  /** Conversation only: repeat errors, one per line. */
  note: string;
}

export function emptyDraft(component: ManualComponent = 'course'): LogDraft {
  return { component, minutes: '', note: '' };
}

/** Only the conversation component asks for anything beyond minutes. */
export function takesNote(component: ManualComponent): boolean {
  return component === 'conversation';
}

// ---------------------------------------------------------------------------------------------
// Repeat errors → candidate items
// ---------------------------------------------------------------------------------------------

/**
 * One error, as written down.
 *
 * A line is read as `español = english`, and `=`, `—`, `–` and ` - ` all separate. That format is a
 * requirement rather than a nicety: an item needs both halves to be askable, and a line with only
 * one of them would become a card that cannot be answered — a review that rots in the queue forever
 * because there is no correct response to it. See DECISIONS D-226.
 */
export interface ParsedError {
  es: string;
  en: string;
}

const SEPARATOR = /\s*(?:=|—|–|\s-\s)\s*/;

/**
 * Read the note into candidate pairs.
 *
 * Lines that carry no separator are **skipped, not guessed at**. The screen reports how many became
 * items, so a line that did not is visible rather than silently dropped — and the note itself is
 * kept on the entry regardless, because what he wrote is worth keeping whether or not this could
 * parse it.
 */
export function parseErrors(note: string): ParsedError[] {
  return note
    .split(/[\n;]+/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => line.split(SEPARATOR))
    .filter((parts): parts is [string, string, ...string[]] => parts.length >= 2)
    .map(([es, ...rest]) => ({ es: es.trim(), en: rest.join(' ').trim() }))
    .filter((pair) => pair.es.length > 0 && pair.en.length > 0);
}

/**
 * A candidate item minted from a conversation error (DoD-11).
 *
 * **Tier 2**, deliberately. Not tier 5, which is where a phrase would naturally sit: tier 5 is behind
 * three accuracy gates, and an item the learner cannot reach is an item that does not exist. Tier 2
 * is unlocked from the first launch, so a mistake made on Tuesday can come back on Wednesday — which
 * is the entire value of capturing it.
 *
 * `vetted: false`, like everything else, and `source: 'conversation-log'` so the review screen and
 * the export can both tell it apart from the seed corpus.
 */
export function candidateItem(error: ParsedError, at: number, index: number): ItemRecord {
  return {
    id: candidateId(error.es, at, index),
    tier: 2,
    es: error.es,
    en: error.en,
    // Cloze degrades to a plain translation prompt with no carrier sentence, which is exactly right
    // here: there is no sentence, and inventing one would be putting words in Diana's mouth.
    modes: ['cloze'],
    tags: ['conversation'],
    vetted: false,
    verdict: null,
    source: 'conversation-log',
    memory: newMemory(at),
    failureCount: 0,
  };
}

/**
 * A stable, legal id.
 *
 * Derived from the Spanish plus the entry's timestamp, so logging the same mistake twice in one
 * session cannot collide, and logging it again next month makes a second item rather than silently
 * overwriting the first — two sightings of the same error are two facts about his Spanish.
 */
export function candidateId(es: string, at: number, index: number): string {
  const slug =
    es
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 24) || 'nota';
  return `conv-${slug}-${at.toString(36)}-${index}`;
}

// ---------------------------------------------------------------------------------------------
// The entry
// ---------------------------------------------------------------------------------------------

export interface LoggedSession {
  entry: ManualEntry;
  /** Items to write alongside it. Empty for every component but conversation. */
  candidates: ItemRecord[];
}

export interface LogInput {
  draft: LogDraft;
  minutes: number;
  at: number;
  /** Deterministic id for the entry itself. */
  id: string;
}

/**
 * Build the entry and whatever it generates.
 *
 * The note is kept on the entry **whether or not it parsed** — it is the learner's own record of a
 * conversation, and a program that discarded it because it could not read it would be deciding that
 * its own convenience outranks his notes.
 */
export function logSession(input: LogInput): LoggedSession {
  const { draft, at } = input;
  const note = draft.note.trim();
  const errors = takesNote(draft.component) ? parseErrors(note) : [];
  const candidates = errors.map((error, index) => candidateItem(error, at, index));

  const entry: ManualEntry = {
    id: input.id,
    component: draft.component,
    minutes: input.minutes,
    at,
    ...(note.length > 0 ? { note } : {}),
    ...(candidates.length > 0 ? { derivedItemIds: candidates.map((item) => item.id) } : {}),
  };

  return { entry, candidates };
}

/** What the screen says after saving. A receipt, never a congratulation. */
export function logReceipt(result: LoggedSession): string {
  const minutes = `${result.entry.minutes} min logged`;
  if (result.candidates.length === 0) return `${minutes}.`;
  const items = result.candidates.length === 1 ? '1 item' : `${result.candidates.length} items`;
  return `${minutes}. ${items} added to the queue.`;
}
